import React, { useEffect, useState, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Printer, MessageCircle, RefreshCw, FileText, CheckCircle2, Save, Loader2, List, ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
import { supabase } from '../../lib/supabase';
const SERVICE_TYPES = ['Menú Digital Fase 1', 'Menú Digital Fase 2', 'Menú Digital Completo', 'Landing Page', 'KDS Sistema Cocina', 'Delivery System', 'Personalizado'];
const PAYMENT_METHODS = ['SINPE Móvil', 'Transferencia Bancaria', 'Efectivo', 'Otro'];
const PAYMENT_TYPES = ['Pago Total', 'Primer Abono (50%)', 'Segundo Abono (50%)', 'Abono Parcial', 'Cotización'];
// Tipo especial: no es un pago, es una propuesta de precio. El documento
// cambia de título y no muestra monto pagado, saldo ni sello de abono.
const QUOTE_TYPE = 'Cotización';
const DEFAULT_DESCRIPTIONS: Record<string, string> = {
  'Menú Digital Fase 1': 'Implementación de Menú Digital Fase 1. Incluye configuración inicial, carga de menú, y código QR.',
  'Menú Digital Fase 2': 'Implementación de Menú Digital Fase 2. Incluye sistema de pedidos, integración con KDS y reportes básicos.',
  'Menú Digital Completo': 'Sistema integral de Menú Digital. Incluye pedidos, KDS, reportes avanzados y módulo de lealtad.',
  'Landing Page': 'Diseño y desarrollo de Landing Page personalizada con integración al menú digital.',
  'KDS Sistema Cocina': 'Implementación de Kitchen Display System (KDS) para gestión de comandas en cocina.',
  'Delivery System': 'Módulo completo de delivery con asignación de choferes y tracking de órdenes.',
  Personalizado: 'Servicios de desarrollo y configuración personalizados.'
};
// ─── Line items + discount: encoded into service_description ─────────
// We persist the extra data (lineItems[], discount) as a JSON marker
// appended to service_description so we don't need a DB migration and
// existing saved receipts stay 100% backwards compatible:
//   <plain text description>
//   <!--__DETAILS__:{"items":[...],"discount":1234}-->
// On load, we split on the marker; the plain text stays in the
// description textarea, the JSON repopulates the structured fields.
const DETAILS_MARKER = '<!--__DETAILS__:';
const DETAILS_END = '-->';
type LineItem = {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
};
const newLineItem = (): LineItem => ({
  id: Math.random().toString(36).slice(2, 10),
  description: '',
  quantity: 1,
  unitPrice: 0
});
const encodeDescription = (plain: string, items: LineItem[], discount: number): string => {
  if (items.length === 0 && (!discount || discount === 0)) return plain;
  const payload = JSON.stringify({
    items: items.map(it => ({
      description: it.description,
      quantity: it.quantity,
      unitPrice: it.unitPrice
    })),
    discount: discount || 0
  });
  return `${plain}\n\n${DETAILS_MARKER}${payload}${DETAILS_END}`;
};
const decodeDescription = (raw: string): {
  plain: string;
  items: LineItem[];
  discount: number;
} => {
  if (!raw || !raw.includes(DETAILS_MARKER)) {
    return {
      plain: raw || '',
      items: [],
      discount: 0
    };
  }
  try {
    const idx = raw.indexOf(DETAILS_MARKER);
    const plain = raw.slice(0, idx).trimEnd();
    const after = raw.slice(idx + DETAILS_MARKER.length);
    const endIdx = after.indexOf(DETAILS_END);
    const json = endIdx >= 0 ? after.slice(0, endIdx) : after;
    const parsed = JSON.parse(json);
    const items: LineItem[] = (parsed.items || []).map((it: any) => ({
      id: Math.random().toString(36).slice(2, 10),
      description: String(it.description || ''),
      quantity: Number(it.quantity) || 0,
      unitPrice: Number(it.unitPrice) || 0
    }));
    return {
      plain,
      items,
      discount: Number(parsed.discount) || 0
    };
  } catch {
    return {
      plain: raw,
      items: [],
      discount: 0
    };
  }
};
export function ReceiptGeneratorPage() {
  const navigate = useNavigate();
  const getNextReceiptNumber = async (): Promise<string> => {
    const year = new Date().getFullYear();
    const prefix = `NOIZA-${year}-`;
    try {
      const {
        data
      } = await supabase.from('noiza_receipts').select('receipt_number').like('receipt_number', `${prefix}%`).order('receipt_number', {
        ascending: false
      }).limit(1);
      if (data && data.length > 0) {
        const lastNum = parseInt(data[0].receipt_number.replace(prefix, ''), 10);
        return `${prefix}${String((lastNum || 0) + 1).padStart(4, '0')}`;
      }
      return `${prefix}0001`;
    } catch {
      return `${prefix}0001`;
    }
  };
  const [formData, setFormData] = useState({
    receiptNumber: '',
    date: new Date().toISOString().split('T')[0],
    clientName: '',
    clientId: '',
    clientPhone: '',
    serviceType: 'Menú Digital Fase 1',
    serviceDescription: DEFAULT_DESCRIPTIONS['Menú Digital Fase 1'],
    totalAmount: 100000,
    paymentAmount: 50000,
    paymentMethod: 'SINPE Móvil',
    paymentReference: '',
    paymentType: 'Primer Abono (50%)',
    notes: ''
  });
  // Line items and discount (new — optional desglose)
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [discount, setDiscount] = useState<number>(0);
  const [savedReceipts, setSavedReceipts] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showSavedList, setShowSavedList] = useState(false);
  const [loadingReceipts, setLoadingReceipts] = useState(false);
  // Derived totals — when there are line items, the total is computed
  // from them minus the discount; the user's manually-entered
  // totalAmount becomes informational only and is auto-synced.
  const lineItemsSubtotal = lineItems.reduce((sum, it) => sum + (Number(it.quantity) || 0) * (Number(it.unitPrice) || 0), 0);
  const hasLineItems = lineItems.length > 0;
  const effectiveTotal = hasLineItems ? Math.max(0, lineItemsSubtotal - (Number(discount) || 0)) : formData.totalAmount;
  const remainingBalance = effectiveTotal - formData.paymentAmount;
  const isQuote = formData.paymentType === QUOTE_TYPE;
  // Keep formData.totalAmount in sync with the computed total whenever
  // line items or discount change, so the existing flows (payment %,
  // saved totalAmount on the DB, etc.) keep working unchanged.
  useEffect(() => {
    if (hasLineItems) {
      setFormData(prev => {
        if (prev.totalAmount === effectiveTotal) return prev;
        return {
          ...prev,
          totalAmount: effectiveTotal
        };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItemsSubtotal, discount, hasLineItems]);
  useEffect(() => {
    fetchReceipts();
    // Set initial sequential receipt number
    getNextReceiptNumber().then(num => {
      setFormData(prev => ({
        ...prev,
        receiptNumber: num
      }));
    });
  }, []);
  const fetchReceipts = async () => {
    setLoadingReceipts(true);
    try {
      const {
        data,
        error
      } = await supabase.from('noiza_receipts').select('*').order('created_at', {
        ascending: false
      }).limit(20);
      if (error) throw error;
      if (data) setSavedReceipts(data);
    } catch (err) {
      console.error('Error fetching receipts:', err);
    } finally {
      setLoadingReceipts(false);
    }
  };
  const handleSave = async () => {
    if (!formData.clientName) {
      alert('Por favor ingresa el nombre del cliente');
      return;
    }
    setSaving(true);
    try {
      // Check if receipt number already exists
      const {
        data: existing
      } = await supabase.from('noiza_receipts').select('id').eq('receipt_number', formData.receiptNumber).single();
      const payload = {
        receipt_number: formData.receiptNumber,
        receipt_date: formData.date,
        client_name: formData.clientName,
        client_id: formData.clientId,
        client_phone: formData.clientPhone,
        service_type: formData.serviceType,
        // Encode line items + discount into service_description so the
        // existing DB schema stays untouched.
        service_description: encodeDescription(formData.serviceDescription, lineItems, discount),
        total_amount: effectiveTotal,
        payment_amount: formData.paymentAmount,
        remaining_balance: remainingBalance,
        payment_method: formData.paymentMethod,
        payment_reference: formData.paymentReference,
        payment_type: formData.paymentType,
        notes: formData.notes
      };
      let error;
      if (existing) {
        // Update
        const {
          error: updateError
        } = await supabase.from('noiza_receipts').update(payload).eq('id', existing.id);
        error = updateError;
      } else {
        // Insert
        const {
          error: insertError
        } = await supabase.from('noiza_receipts').insert([payload]);
        error = insertError;
      }
      if (error) throw error;
      setSaveSuccess(true);
      fetchReceipts();
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving receipt:', err);
      alert('Error al guardar el recibo. Revisa la consola para más detalles.');
    } finally {
      setSaving(false);
    }
  };
  const loadReceipt = (receipt: any) => {
    const decoded = decodeDescription(receipt.service_description || '');
    setFormData({
      receiptNumber: receipt.receipt_number,
      date: receipt.receipt_date,
      clientName: receipt.client_name,
      clientId: receipt.client_id || '',
      clientPhone: receipt.client_phone || '',
      serviceType: receipt.service_type,
      serviceDescription: decoded.plain,
      totalAmount: receipt.total_amount,
      paymentAmount: receipt.payment_amount,
      paymentMethod: receipt.payment_method,
      paymentReference: receipt.payment_reference || '',
      paymentType: receipt.payment_type,
      notes: receipt.notes || ''
    });
    setLineItems(decoded.items);
    setDiscount(decoded.discount);
    // Scroll to top
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const handleServiceTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value;
    setFormData(prev => ({
      ...prev,
      serviceType: newType,
      serviceDescription: DEFAULT_DESCRIPTIONS[newType] || ''
    }));
  };
  const handleReset = async () => {
    const nextNum = await getNextReceiptNumber();
    setFormData({
      receiptNumber: nextNum,
      date: new Date().toISOString().split('T')[0],
      clientName: '',
      clientId: '',
      clientPhone: '',
      serviceType: 'Menú Digital Fase 1',
      serviceDescription: DEFAULT_DESCRIPTIONS['Menú Digital Fase 1'],
      totalAmount: 100000,
      paymentAmount: 50000,
      paymentMethod: 'SINPE Móvil',
      paymentReference: '',
      paymentType: 'Primer Abono (50%)',
      notes: ''
    });
    setLineItems([]);
    setDiscount(0);
  };
  const handlePrint = () => {
    window.print();
  };
  const handleWhatsAppShare = () => {
    const text = isQuote ? `*COTIZACIÓN - NOIZA* 📄

*Cotización N°:* ${formData.receiptNumber}
*Fecha:* ${formData.date}
*Cliente:* ${formData.clientName}

*Servicio:* ${formData.serviceType}

*Total Cotizado:* ${formatCurrency(effectiveTotal)}

Gracias por confiar en NOIZA.
Plataforma de Delivery Digital - Costa Rica
noizacr.com` : `*RECIBO DE PAGO - NOIZA* 🧾

*Recibo N°:* ${formData.receiptNumber}
*Fecha:* ${formData.date}
*Cliente:* ${formData.clientName}

*Servicio:* ${formData.serviceType}
*Tipo de Pago:* ${formData.paymentType}
*Método:* ${formData.paymentMethod} ${formData.paymentReference ? `(Ref: ${formData.paymentReference})` : ''}

*Monto Pagado:* ${formatCurrency(formData.paymentAmount)}
*Saldo Pendiente:* ${formatCurrency(remainingBalance)}

Gracias por confiar en NOIZA.
Plataforma de Delivery Digital - Costa Rica
noizacr.com`;
    const encodedText = encodeURIComponent(text);
    let phone = formData.clientPhone.replace(/\D/g, '');
    if (phone && !phone.startsWith('506')) {
      phone = '506' + phone;
    }
    const url = phone ? `https://wa.me/${phone}?text=${encodedText}` : `https://wa.me/?text=${encodedText}`;
    window.open(url, '_blank');
  };
  // Line item handlers
  const addLineItem = () => setLineItems(prev => [...prev, newLineItem()]);
  const updateLineItem = (id: string, field: keyof Omit<LineItem, 'id'>, value: string | number) => setLineItems(prev => prev.map(it => it.id === id ? {
    ...it,
    [field]: field === 'description' ? String(value) : Number(value) || 0
  } : it));
  const removeLineItem = (id: string) => setLineItems(prev => prev.filter(it => it.id !== id));
  return <div className="min-h-screen bg-[#0B0B0B] text-white print:bg-white print:text-black" data-id="element-6154" data-mp="354349zd983zv">
      {/* Print Styles */}
      <style data-id="element-6155" data-mp="30a8ece722ssc">
        {`
          @media print {
            * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            html, body { background: white !important; margin: 0 !important; padding: 0 !important; }
            .no-print { display: none !important; }
            .print-only { display: block !important; }
            .receipt-container {
              box-shadow: none !important;
              border: none !important;
              padding: 24px !important;
              margin: 0 auto !important;
              width: 100% !important;
              max-width: 100% !important;
              border-radius: 0 !important;
              background: white !important;
              color: black !important;
            }
            .print-text-black { color: black !important; }
            .print-border-black { border-color: #e5e7eb !important; }
            .print-bg-gray { background-color: #f9fafb !important; }
          }
        `}
      </style>

      {/* Header - Hidden in print */}
      <div className="no-print sticky top-0 z-10 bg-[#0B0B0B]/95 backdrop-blur-md border-b border-white/10 px-4 sm:px-6 py-4" data-id="element-6156" data-mp="dnub0xazoeds">
        <div className="max-w-7xl mx-auto flex items-center justify-between" data-id="element-6157" data-mp="3w1k1i0miuerd">
          <div className="flex items-center gap-4" data-id="element-6158" data-mp="4oz57mbux6b0">
            <button onClick={() => navigate('/admin/enrollment')} className="p-2 hover:bg-white/5 rounded-lg transition-colors" data-id="element-6159" data-mp="3m4xk5ltem5u3">
              <ArrowLeft className="w-5 h-5 text-white/70" data-id="element-6160" data-mp="3r1o1ecgy37h3" />
            </button>
            <div data-id="element-6161" data-mp="3d4iwwgwvqgny">
              <h1 className="text-xl font-bold text-white" data-id="element-6162" data-mp="3d63qa37gjtk8">
                Generador de Recibos
              </h1>
              <p className="text-sm text-white/50" data-id="element-6163" data-mp="2yhahf3wjfp66">NOIZA Admin</p>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-6164" data-mp="2ti7b7g4tncwm">
            <button onClick={handleReset} className="px-4 py-2 bg-white/5 hover:bg-white/10 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2" data-id="element-6165" data-mp="2tjiny5g8vuzv">
              <RefreshCw className="w-4 h-4" data-id="element-6166" data-mp="32hkm8d8lg9jg" />
              <span className="hidden sm:inline" data-id="element-6167" data-mp="3hfmpembpqt06">Nuevo</span>
            </button>

            <button onClick={handleSave} disabled={saving} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${saveSuccess ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'}`} data-id="element-6168" data-mp="3ch36yvwbvzck">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" data-id="element-6169" data-mp="421glcctrtee" /> : saveSuccess ? <CheckCircle2 className="w-4 h-4" data-id="element-6170" data-mp="3v8vw2f8a10f0" /> : <Save className="w-4 h-4" data-id="element-6171" data-mp="2suok3c3p56ug" />}
              <span className="hidden sm:inline" data-id="element-6172" data-mp="2ss25isie1r2q">
                {saving ? 'Guardando...' : saveSuccess ? 'Guardado' : 'Guardar'}
              </span>
            </button>

            <button onClick={handleWhatsAppShare} className="px-4 py-2 bg-[#25D366]/20 text-[#25D366] hover:bg-[#25D366]/30 rounded-lg text-sm font-medium transition-colors flex items-center gap-2" data-id="element-6173" data-mp="31ta132tisdt4">
              <MessageCircle className="w-4 h-4" data-id="element-6174" data-mp="3gjpo8n5hxvtx" />
              <span className="hidden sm:inline" data-id="element-6175" data-mp="3gieiihshqe06">WhatsApp</span>
            </button>
            <button onClick={handlePrint} className="px-4 py-2 bg-[#C9A34E] text-[#0B0B0B] hover:bg-[#C9A34E]/90 rounded-lg text-sm font-bold transition-colors flex items-center gap-2" data-id="element-6176" data-mp="3kl2qyodaliwm">
              <Printer className="w-4 h-4" data-id="element-6177" data-mp="1q8uw1b0z120u" />
              <span className="hidden sm:inline" data-id="element-6178" data-mp="1v64pd1rhdxty">Imprimir / PDF</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-id="element-6179" data-mp="1nnx3ich6ge3i">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-6180" data-mp="1iw5dq45uicef">
          {/* Form Section */}
          <div className="lg:col-span-5 space-y-6 no-print" data-id="element-6181" data-mp="1dx274fa7vy4c">
            <div className="bg-[#141414] border border-white/10 rounded-2xl p-6" data-id="element-6182" data-mp="1dxls64v5z2ml">
              <h2 className="text-lg font-bold text-white mb-6 flex items-center gap-2" data-id="element-6183" data-mp="18zaefj1r07z3">
                <FileText className="w-5 h-5 text-[#C9A34E]" data-id="element-6184" data-mp="26kojod1fad3c" />
                Datos del Recibo
              </h2>

              <div className="space-y-5" data-id="element-6185" data-mp="21ny2fmdvtbgc">
                <div className="grid grid-cols-2 gap-4" data-id="element-6186" data-mp="1wnjwc9vucbky">
                  <div data-id="element-6187" data-mp="1wlz08ftvokkz">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-6188" data-mp="1wpmszcri12yn">
                      N° de Recibo
                    </label>
                    <input type="text" value={formData.receiptNumber} onChange={e => setFormData({
                    ...formData,
                    receiptNumber: e.target.value
                  })} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-6189" data-mp="l8n2mmopoy79" />
                  </div>
                  <div data-id="element-6190" data-mp="2cc2c48v73q7d">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-6191" data-mp="2zzix0qg4sioa">
                      Fecha
                    </label>
                    <input type="date" value={formData.date} onChange={e => setFormData({
                    ...formData,
                    date: e.target.value
                  })} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-6192" data-mp="33zuddr1gnwty" />
                  </div>
                </div>

                <div className="space-y-4" data-id="element-6193" data-mp="w6ed75iooq9">
                  <h3 className="text-sm font-semibold text-white/80 border-b border-white/10 pb-2" data-id="element-6194" data-mp="3t1a90vjy4d3b">
                    Cliente
                  </h3>
                  <div data-id="element-6195" data-mp="aq6qvu34ow90">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-6196" data-mp="5or3hvdhadng">
                      Nombre / Razón Social
                    </label>
                    <input type="text" value={formData.clientName} onChange={e => setFormData({
                    ...formData,
                    clientName: e.target.value
                  })} placeholder="Ej. Restaurante El Sabor" className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-6197" data-mp="3e75daau1kwqn" />
                  </div>
                  <div className="grid grid-cols-2 gap-4" data-id="element-6198" data-mp="1ykr03uirdohl">
                    <div data-id="element-6199" data-mp="1tklgn1mn78al">
                      <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-6200" data-mp="1tled99rw0hf0">
                        Cédula (Opcional)
                      </label>
                      <input type="text" value={formData.clientId} onChange={e => setFormData({
                      ...formData,
                      clientId: e.target.value
                    })} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-6201" data-mp="23hhpj8ggdwfo" />
                    </div>
                    <div data-id="element-6202" data-mp="305nphi2fk0nm">
                      <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-6203" data-mp="2v96z0h7rda7d">
                        Teléfono / WhatsApp
                      </label>
                      <input type="text" value={formData.clientPhone} onChange={e => setFormData({
                      ...formData,
                      clientPhone: e.target.value
                    })} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-6204" data-mp="3esmdt45zqey4" />
                    </div>
                  </div>
                </div>

                <div className="space-y-4" data-id="element-6205" data-mp="2m471ipe8uaqh">
                  <h3 className="text-sm font-semibold text-white/80 border-b border-white/10 pb-2" data-id="element-6206" data-mp="2r4d06pixkxi2">
                    Servicio
                  </h3>
                  <div data-id="element-6207" data-mp="2d7pofgmbk2s3">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-6208" data-mp="2i46nh5v241jy">
                      Tipo de Servicio
                    </label>
                    <select value={formData.serviceType} onChange={handleServiceTypeChange} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-6209" data-mp="npj1npw1gnbd">
                      {SERVICE_TYPES.map(type => <option key={type} value={type} data-id="element-6210" data-mp="16m12qxws1zka">
                          {type}
                        </option>)}
                    </select>
                  </div>
                  <div data-id="element-6211" data-mp="xlcoc23h1x6v">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-6212" data-mp="xkkq0jihplcw">
                      Descripción
                    </label>
                    <textarea value={formData.serviceDescription} onChange={e => setFormData({
                    ...formData,
                    serviceDescription: e.target.value
                  })} rows={3} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E] resize-none" data-id="element-6213" data-mp="3id620in7vrsz" />
                  </div>
                </div>

                {/* ─── Desglose (Line Items) ───────────────────── */}
                <div className="space-y-3" data-id="element-6214" data-mp="2blqsw5nyiowg">
                  <div className="flex items-center justify-between border-b border-white/10 pb-2" data-id="element-6215" data-mp="2gkk9i6yjiw0u">
                    <h3 className="text-sm font-semibold text-white/80" data-id="element-6216" data-mp="2ljn8b9nxg60d">
                      Desglose del Cobro{' '}
                      <span className="text-xs text-white/40 font-normal" data-id="element-6217" data-mp="2pkqhxefdt5t5">
                        (Opcional)
                      </span>
                    </h3>
                    <button type="button" onClick={addLineItem} className="text-xs font-medium text-[#C9A34E] hover:text-[#C9A34E]/80 flex items-center gap-1" data-id="element-6218" data-mp="v7h6vi76tc8s">
                      <Plus className="w-3.5 h-3.5" data-id="element-6219" data-mp="3s0tohato7jl1" />
                      Agregar línea
                    </button>
                  </div>

                  {lineItems.length === 0 ? <p className="text-xs text-white/40 italic" data-id="element-6220" data-mp="4xyp4pfl79d9">
                      Sin desglose. El recibo usará el monto total único de
                      arriba. Agregá líneas para desglosar por
                      producto/servicio.
                    </p> : <div className="space-y-2" data-id="element-6221" data-mp="3n3bmk4e2m4mj">
                      {lineItems.map((it, idx) => {
                    const subtotal = (Number(it.quantity) || 0) * (Number(it.unitPrice) || 0);
                    return <div key={it.id} className="bg-[#0B0B0B] border border-white/10 rounded-lg p-3 space-y-2" data-id="element-6222" data-mp="2pno02rd66b0o" data-mpi={idx}>
                            <div className="flex items-center justify-between" data-id="element-6223" data-mp="19vtuqjzeq8lv">
                              <span className="text-[10px] uppercase tracking-wider text-white/40 font-bold" data-id="element-6224" data-mp="w4f9s7lxvxc6">
                                Línea {idx + 1}
                              </span>
                              <button type="button" onClick={() => removeLineItem(it.id)} className="text-white/30 hover:text-red-400 transition-colors" aria-label="Eliminar línea" data-id="element-6225" data-mp="1nvl6sf6d5ex8">
                                <Trash2 className="w-3.5 h-3.5" data-id="element-6226" data-mp="1jsf5lly3ydxm" />
                              </button>
                            </div>
                            <input type="text" value={it.description} onChange={e => updateLineItem(it.id, 'description', e.target.value)} placeholder="Descripción del producto/servicio" className="w-full bg-[#141414] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-6227" data-mp="3hulxh4oa3dd" />
                            <div className="grid grid-cols-3 gap-2" data-id="element-6228" data-mp="1hf36ukzaslem">
                              <div data-id="element-6229" data-mp="1hfcwg7gfkqjs">
                                <label className="block text-[10px] font-medium text-white/40 mb-1" data-id="element-6230" data-mp="1ddrck25hbc03">
                                  Cantidad
                                </label>
                                <input type="number" min={0} step={1} value={it.quantity} onChange={e => updateLineItem(it.id, 'quantity', e.target.value)} className="w-full bg-[#141414] border border-white/10 rounded-lg px-2 py-1.5 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-6231" data-mp="3bqroiuwwz22p" />
                              </div>
                              <div data-id="element-6232" data-mp="jnwsv1clbtvh">
                                <label className="block text-[10px] font-medium text-white/40 mb-1" data-id="element-6233" data-mp="okwyjb5j2um2">
                                  Precio Unit. ₡
                                </label>
                                <input type="number" min={0} value={it.unitPrice} onChange={e => updateLineItem(it.id, 'unitPrice', e.target.value)} className="w-full bg-[#141414] border border-white/10 rounded-lg px-2 py-1.5 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-6234" data-mp="1zzai43t90nwa" />
                              </div>
                              <div data-id="element-6235" data-mp="23dmqa6n7k1bl">
                                <label className="block text-[10px] font-medium text-white/40 mb-1" data-id="element-6236" data-mp="1zd3ww85o2n7a">
                                  Subtotal
                                </label>
                                <div className="w-full bg-[#141414] border border-white/10 rounded-lg px-2 py-1.5 text-[#C9A34E] text-sm font-bold" data-id="element-6237" data-mp="927xfgywhlrm">
                                  {formatCurrency(subtotal)}
                                </div>
                              </div>
                            </div>
                          </div>;
                  })}

                      {/* Subtotal + Discount summary inside form */}
                      <div className="bg-[#0B0B0B] border border-white/10 rounded-lg p-3 space-y-2" data-id="element-6238" data-mp="3m7zecv7xmbfu">
                        <div className="flex justify-between text-xs text-white/60" data-id="element-6239" data-mp="37aq8yxlsn754">
                          <span data-id="element-6240" data-mp="3cc5qiforivir">Subtotal líneas</span>
                          <span className="font-bold text-white" data-id="element-6241" data-mp="2ybuzncngdgxl">
                            {formatCurrency(lineItemsSubtotal)}
                          </span>
                        </div>
                        <div data-id="element-6242" data-mp="wk8gtydpssc7">
                          <label className="block text-[10px] font-medium text-white/40 mb-1" data-id="element-6243" data-mp="wjyquaso6l6i">
                            Descuento ₡
                          </label>
                          <input type="number" min={0} value={discount} onChange={e => setDiscount(Number(e.target.value) || 0)} placeholder="0" className="w-full bg-[#141414] border border-white/10 rounded-lg px-2 py-1.5 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-6244" data-mp="23azbvmyh5fdz" />
                        </div>
                        <div className="flex justify-between text-sm pt-2 border-t border-white/10" data-id="element-6245" data-mp="2qyp9wrjcwkj6">
                          <span className="text-white font-semibold" data-id="element-6246" data-mp="2w0enoexyrcay">
                            Total
                          </span>
                          <span className="font-bold text-[#C9A34E]" data-id="element-6247" data-mp="11lvig4m1eup8">
                            {formatCurrency(effectiveTotal)}
                          </span>
                        </div>
                      </div>
                    </div>}
                </div>

                {/* ─── Existing Pago section ──────────────────── */}
                <div className="space-y-4" data-id="element-6248" data-mp="3f2ympxyyhe3e">
                  <h3 className="text-sm font-semibold text-white/80 border-b border-white/10 pb-2" data-id="element-6249" data-mp="3b24x905v7r8e">
                    Pago
                  </h3>
                  <div className="grid grid-cols-2 gap-4" data-id="element-6250" data-mp="365eicg5sr1ok">
                    <div data-id="element-6251" data-mp="6opyt37dpbhk">
                      <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-6252" data-mp="6nwoxtd4i5v7">
                        Monto Total (Contrato)
                        {hasLineItems && <span className="ml-1 text-[10px] text-white/40" data-id="element-6253" data-mp="3ts34xf5r3ibi">
                            (auto)
                          </span>}
                      </label>
                      <div className="relative" data-id="element-6254" data-mp="2if0n6grx0mpr">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" data-id="element-6255" data-mp="2ibmllar2shjd">
                          ₡
                        </span>
                        <input type="number" value={formData.totalAmount} onChange={e => setFormData({
                        ...formData,
                        totalAmount: Number(e.target.value)
                      })} disabled={hasLineItems} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg pl-8 pr-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E] disabled:opacity-60 disabled:cursor-not-allowed" data-id="element-6256" data-mp="2excygv26q6lg" />
                      </div>
                    </div>
                    <div data-id="element-6257" data-mp="1r6qpn1kcwiae">
                      <label className="block text-xs font-medium text-[#C9A34E] mb-1.5" data-id="element-6258" data-mp="1d6podl06j8ue">
                        Monto de este Pago
                      </label>
                      <div className="relative" data-id="element-6259" data-mp="1i59ablerwkmr">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#C9A34E]/60" data-id="element-6260" data-mp="yjr4cb3njgpp">
                          ₡
                        </span>
                        <input type="number" value={formData.paymentAmount} onChange={e => setFormData({
                        ...formData,
                        paymentAmount: Number(e.target.value)
                      })} className="w-full bg-[#C9A34E]/10 border border-[#C9A34E]/30 rounded-lg pl-8 pr-3 py-2 text-[#C9A34E] font-bold text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-6261" data-mp="tn0mbi8aeb1n" />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4" data-id="element-6262" data-mp="20a5x9h2i4mzr">
                    <div data-id="element-6263" data-mp="1w811ocshtgah">
                      <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-6264" data-mp="1w8soay4443om">
                        Tipo de Pago
                      </label>
                      <select value={formData.paymentType} onChange={e => setFormData({
                      ...formData,
                      paymentType: e.target.value
                    })} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-6265" data-mp="3upl3rctkweju">
                        {PAYMENT_TYPES.map(type => <option key={type} value={type} data-id="element-6266" data-mp="3go8ol3m8sgz0">
                            {type}
                          </option>)}
                      </select>
                    </div>
                    <div data-id="element-6267" data-mp="vzb7a6yp52z8">
                      <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-6268" data-mp="1016694t5n49u">
                        Método de Pago
                      </label>
                      <select value={formData.paymentMethod} onChange={e => setFormData({
                      ...formData,
                      paymentMethod: e.target.value
                    })} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-6269" data-mp="2kip4qhmlch01">
                        {PAYMENT_METHODS.map(method => <option key={method} value={method} data-id="element-6270" data-mp="1sr97zurbldw3">
                            {method}
                          </option>)}
                      </select>
                    </div>
                  </div>

                  <div data-id="element-6271" data-mp="3n5agz8gl3h9w">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-6272" data-mp="3n3pjpb2vxk8c">
                      Referencia (Opcional)
                    </label>
                    <input type="text" value={formData.paymentReference} onChange={e => setFormData({
                    ...formData,
                    paymentReference: e.target.value
                  })} placeholder="Ej. Comprobante #123456" className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-6273" data-mp="55enmvfiprd7" />
                  </div>

                  <div data-id="element-6274" data-mp="evhqdncfyjoq">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-6275" data-mp="eydk0zh9am7d">
                      Notas Adicionales
                    </label>
                    <textarea value={formData.notes} onChange={e => setFormData({
                    ...formData,
                    notes: e.target.value
                  })} rows={2} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E] resize-none" data-id="element-6276" data-mp="2zlgshg6t1yjf" />
                  </div>
                </div>
              </div>
            </div>

            {/* Saved Receipts Panel */}
            <div className="bg-[#141414] border border-white/10 rounded-2xl overflow-hidden" data-id="element-6277" data-mp="2dbqfbwy6yxub">
              <button onClick={() => setShowSavedList(!showSavedList)} className="w-full flex items-center justify-between p-6 hover:bg-white/5 transition-colors" data-id="element-6278" data-mp="29a4zc2qd23fw">
                <h2 className="text-lg font-bold text-white flex items-center gap-2" data-id="element-6279" data-mp="2ib32yjww08xy">
                  <List className="w-5 h-5 text-blue-400" data-id="element-6280" data-mp="1ul8qi3ghcme8" />
                  Recibos Guardados
                  <span className="bg-white/10 text-white/70 text-xs py-0.5 px-2 rounded-full ml-2" data-id="element-6281" data-mp="1pm5z3ltfaeoq">
                    {savedReceipts.length}
                  </span>
                </h2>
                {showSavedList ? <ChevronUp className="w-5 h-5 text-white/50" data-id="element-6282" data-mp="1e33h38gbfsgx" /> : <ChevronDown className="w-5 h-5 text-white/50" data-id="element-6283" data-mp="1e5g4400xkt0u" />}
              </button>

              {showSavedList && <div className="border-t border-white/10 p-4 max-h-96 overflow-y-auto custom-scrollbar" data-id="element-6284" data-mp="1o082v7691lx7">
                  {loadingReceipts ? <div className="flex items-center justify-center py-8" data-id="element-6285" data-mp="1s0s1kf6h0xlg">
                      <Loader2 className="w-6 h-6 text-white/30 animate-spin" data-id="element-6286" data-mp="1wu4mae814k8x" />
                    </div> : savedReceipts.length === 0 ? <div className="text-center py-8 text-white/40 text-sm" data-id="element-6287" data-mp="26sal2myjha9k">
                      No hay recibos guardados aún.
                    </div> : <div className="space-y-2" data-id="element-6288" data-mp="3ifduro5647">
                      {savedReceipts.map(receipt => <button key={receipt.id} onClick={() => loadReceipt(receipt)} className="w-full text-left bg-[#0B0B0B] hover:bg-white/5 border border-white/5 rounded-xl p-4 transition-colors group flex items-center justify-between" data-id="element-6289" data-mp="3ra2z0tfe9br7">
                          <div data-id="element-6290" data-mp="itgli5bwop57">
                            <div className="flex items-center gap-2 mb-1" data-id="element-6291" data-mp="ivud247dnj80">
                              <span className="text-sm font-bold text-white" data-id="element-6292" data-mp="dvouddj35722">
                                {receipt.receipt_number}
                              </span>
                              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md bg-white/10 text-white/70" data-id="element-6293" data-mp="442ciyc8s9qc">
                                {receipt.payment_type}
                              </span>
                            </div>
                            <p className="text-sm text-white/70 font-medium" data-id="element-6294" data-mp="2s7sv6m0ro10e">
                              {receipt.client_name}
                            </p>
                            <p className="text-xs text-white/40 mt-1" data-id="element-6295" data-mp="2n97smfy1l58s">
                              {new Date(receipt.receipt_date).toLocaleDateString('es-CR')}{' '}
                              • {receipt.service_type}
                            </p>
                          </div>
                          <div className="text-right" data-id="element-6296" data-mp="24ihut5nbw8ms">
                            <p className="text-sm font-bold text-[#C9A34E]" data-id="element-6297" data-mp="24gwxx9djkdlr">
                              {formatCurrency(receipt.payment_amount)}
                            </p>
                            <p className="text-xs text-white/40 mt-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-6298" data-mp="1ulbz1k3rrtg8">
                              Ver recibo →
                            </p>
                          </div>
                        </button>)}
                    </div>}
                </div>}
            </div>
          </div>

          {/* Preview Section */}
          <div className="lg:col-span-7 print:col-span-12" data-id="element-6299" data-mp="58wz6gj7ih09">
            <div className="sticky top-24 print:static" data-id="element-6300" data-mp="a3ayuv4wjaaq">
              <h2 className="text-sm font-semibold text-white/60 mb-4 uppercase tracking-wider no-print" data-id="element-6301" data-mp="f0tawue7u5my">
                Vista Previa del Recibo
              </h2>

              {/* THE RECEIPT */}
              <div className="receipt-container bg-white text-black rounded-xl p-8 sm:p-12 shadow-2xl shadow-black/50 max-w-2xl mx-auto border border-white/20 print:shadow-none print:border-none print:p-0 print:max-w-full print:rounded-none" data-id="element-6302" data-mp="3ihjk1i53gysv">
                {/* Header */}
                <div className="flex justify-between items-start mb-10" data-id="element-6303" data-mp="3sdwfakgafkpq">
                  <div data-id="element-6304" data-mp="12q5f1fuornie">
                    <div className="flex items-center gap-2 mb-2" data-id="element-6305" data-mp="12t1ex9q3gm9h">
                      <div className="w-10 h-10 bg-[#0B0B0B] rounded-lg flex items-center justify-center print-bg-gray" data-id="element-6306" data-mp="17ogty4zu8o7h">
                        <span className="text-[#C9A34E] font-bold text-2xl leading-none" data-id="element-6307" data-mp="i3pf8bapt8z1">
                          N
                        </span>
                      </div>
                      <span className="font-bold text-3xl tracking-tight text-[#0B0B0B] print-text-black" style={{
                      fontFamily: "'Playfair Display', serif"
                    }} data-id="element-6308" data-mp="ryh8x416fboj">
                        NOIZA
                      </span>
                    </div>
                    <p className="text-sm text-gray-500" data-id="element-6309" data-mp="91g5vor6nln8">
                      Plataforma de Delivery Digital
                    </p>
                    <p className="text-sm text-gray-500" data-id="element-6310" data-mp="48n0cynw49a3">Costa Rica</p>
                    <p className="text-sm text-gray-500" data-id="element-6311" data-mp="3cp82bvi3u23p">noizacr.com</p>
                  </div>
                  <div className="text-right" data-id="element-6312" data-mp="37rg7otq2s1vt">
                    <h2 className="text-3xl font-black text-gray-200 mb-2 uppercase tracking-widest" data-id="element-6313" data-mp="37qogrw7fasbv">
                      {isQuote ? 'Cotización' : 'Recibo'}
                    </h2>
                    <p className="text-sm font-bold text-gray-800" data-id="element-6314" data-mp="1s16ygge9zqgz">
                      N° {formData.receiptNumber}
                    </p>
                    <p className="text-sm text-gray-500" data-id="element-6315" data-mp="1i9svtzr12ajm">
                      Fecha:{' '}
                      {new Date(formData.date).toLocaleDateString('es-CR')}
                    </p>
                  </div>
                </div>

                {/* Client Info */}
                <div className="mb-8 p-4 bg-gray-50 rounded-lg border border-gray-100 print-bg-gray print-border-black" data-id="element-6316" data-mp="2awdwjqte7yzu">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-6317" data-mp="210aibml9o9wj">
                    {isQuote ? 'Cliente:' : 'Recibí de:'}
                  </h3>
                  <p className="text-lg font-bold text-gray-800" data-id="element-6318" data-mp="llmupmtbe0a3">
                    {formData.clientName || 'Nombre del Cliente'}
                  </p>
                  <div className="flex gap-6 mt-1 text-sm text-gray-600" data-id="element-6319" data-mp="15sjshnk0al0a">
                    {formData.clientId && <p data-id="element-6320" data-mp="10zyyakx6u5w8">
                        <span className="font-semibold" data-id="element-6321" data-mp="10zgw77nugxge">Cédula:</span>{' '}
                        {formData.clientId}
                      </p>}
                    {formData.clientPhone && <p data-id="element-6322" data-mp="1or4c6ujkio3w">
                        <span className="font-semibold" data-id="element-6323" data-mp="1ore5aqzwtbdt">Tel/WA:</span>{' '}
                        {formData.clientPhone}
                      </p>}
                  </div>
                </div>

                {/* Service Details — line items table or legacy */}
                <div className="mb-8" data-id="element-6324" data-mp="1armuez4owb41">
                  <table className="w-full text-left border-collapse" data-id="element-6325" data-mp="1aql8u0q1s682">
                    <thead data-id="element-6326" data-mp="3eb486o7fznv">
                      <tr className="border-b-2 border-gray-200" data-id="element-6327" data-mp="3ekvryoweevo">
                        <th className="py-3 text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-6328" data-mp="3vh2hbj0sffoe">
                          Descripción
                        </th>
                        {hasLineItems && <>
                            <th className="py-3 text-xs font-bold text-gray-400 uppercase tracking-wider text-center w-16" data-id="element-6329" data-mp="2f1ekxcx3wkd1">
                              Cant.
                            </th>
                            <th className="py-3 text-xs font-bold text-gray-400 uppercase tracking-wider text-right w-28" data-id="element-6330" data-mp="2osix64vedr46">
                              Precio
                            </th>
                          </>}
                        <th className="py-3 text-xs font-bold text-gray-400 uppercase tracking-wider text-right w-32" data-id="element-6331" data-mp="32qz5g08wrpof">
                          {hasLineItems ? 'Subtotal' : isQuote ? 'Total' : 'Total Contrato'}
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-6332" data-mp="2gm6tnut11p4y">
                      {hasLineItems ? <>
                          {/* Header row with service type as group title */}
                          <tr className="border-b border-gray-100" data-id="element-6333" data-mp="2pnde2eysrk3h">
                            <td colSpan={4} className="py-3" data-id="element-6334" data-mp="2poohgdobypu2">
                              <p className="font-bold text-gray-800" data-id="element-6335" data-mp="2ko0npk8pvuw5">
                                {formData.serviceType}
                              </p>
                              {formData.serviceDescription && <p className="text-sm text-gray-500 mt-1" data-id="element-6336" data-mp="2ulycc8uhntpa">
                                  {formData.serviceDescription}
                                </p>}
                            </td>
                          </tr>
                          {lineItems.map(it => {
                        const subtotal = (Number(it.quantity) || 0) * (Number(it.unitPrice) || 0);
                        return <tr key={it.id} className="border-b border-gray-100" data-id="element-6337" data-mp="rabjv9jo283">
                                <td className="py-3 pr-4 text-sm text-gray-700" data-id="element-6338" data-mp="3n01xyqdy2o6y">
                                  {it.description || <span className="italic text-gray-400" data-id="element-6339" data-mp="3e89lka5d14zq">
                                      Sin descripción
                                    </span>}
                                </td>
                                <td className="py-3 text-center text-sm text-gray-700" data-id="element-6340" data-mp="34beq604dvkpo">
                                  {it.quantity}
                                </td>
                                <td className="py-3 text-right text-sm text-gray-700" data-id="element-6341" data-mp="2v8vsrvjda0at">
                                  {formatCurrency(it.unitPrice)}
                                </td>
                                <td className="py-3 text-right text-sm font-semibold text-gray-800" data-id="element-6342" data-mp="3fq89m4o9fja1">
                                  {formatCurrency(subtotal)}
                                </td>
                              </tr>;
                      })}
                        </> : <tr className="border-b border-gray-100" data-id="element-6343" data-mp="30spnzb6vpm6t">
                          <td className="py-4 pr-4" data-id="element-6344" data-mp="2ws5npyr0e2gg">
                            <p className="font-bold text-gray-800 mb-1" data-id="element-6345" data-mp="2wtqnq3w3edlp">
                              {formData.serviceType}
                            </p>
                            <p className="text-sm text-gray-500" data-id="element-6346" data-mp="2n2vzitzad3xm">
                              {formData.serviceDescription}
                            </p>
                          </td>
                          <td className="py-4 text-right font-semibold text-gray-800 align-top" data-id="element-6347" data-mp="2d3wmz0bego5c">
                            {formatCurrency(formData.totalAmount)}
                          </td>
                        </tr>}
                    </tbody>
                  </table>
                </div>

                {/* Payment Breakdown */}
                <div className="flex justify-end mb-10" data-id="element-6348" data-mp="sngtt0g19gel">
                  <div className="w-full sm:w-1/2 space-y-3" data-id="element-6349" data-mp="xm0dsvb2gi4b">
                    {hasLineItems && <>
                        <div className="flex justify-between text-sm text-gray-600" data-id="element-6350" data-mp="12hpg36eh0d4b">
                          <span data-id="element-6351" data-mp="4xuu8bvr0z62">Subtotal:</span>
                          <span data-id="element-6352" data-mp="9ul7ysk2ziob">{formatCurrency(lineItemsSubtotal)}</span>
                        </div>
                        {discount > 0 && <div className="flex justify-between text-sm text-red-600" data-id="element-6353" data-mp="eswi74eafv72">
                            <span data-id="element-6354" data-mp="jlpkllmaarfz">Descuento:</span>
                            <span data-id="element-6355" data-mp="3ib7nfm74qfcy">-{formatCurrency(discount)}</span>
                          </div>}
                      </>}
                    {isQuote ? <div className="flex justify-between text-lg font-bold text-[#0B0B0B] border-y border-gray-200 py-3 print-text-black" data-id="element-6356" data-mp="2x0dctt8yubth">
                        <span data-id="element-6357" data-mp="31xvjfczigeyb">Total Cotizado:</span>
                        <span data-id="element-6358" data-mp="2nzp7dmxn1js7">{formatCurrency(effectiveTotal)}</span>
                      </div> : <>
                        <div className="flex justify-between text-sm text-gray-600" data-id="element-6359" data-mp="2rzft3c8b59pv">
                          <span data-id="element-6360" data-mp="2e8kquisbdqv9">Total del Contrato:</span>
                          <span data-id="element-6361" data-mp="2j0cd4h4ftafl">{formatCurrency(effectiveTotal)}</span>
                        </div>
                        <div className="flex justify-between text-lg font-bold text-[#0B0B0B] border-y border-gray-200 py-3 print-text-black" data-id="element-6362" data-mp="24bg1yb733har">
                          <span data-id="element-6363" data-mp="29bax4qxw6t1m">Monto Pagado:</span>
                          <span data-id="element-6364" data-mp="1varnxghoxgxv">{formatCurrency(formData.paymentAmount)}</span>
                        </div>
                        <div className="flex justify-between text-sm font-medium text-gray-500" data-id="element-6365" data-mp="209l45god3m1q">
                          <span data-id="element-6366" data-mp="1gnszlue4r2nd">Saldo Pendiente:</span>
                          <span data-id="element-6367" data-mp="1gob6dkyf4z9j">{formatCurrency(remainingBalance)}</span>
                        </div>
                      </>}
                  </div>
                </div>

                {/* Payment Info & Notes */}
                <div className="grid grid-cols-2 gap-8 mb-12" data-id="element-6368" data-mp="1ln5pyp47mrup">
                  <div data-id="element-6369" data-mp="1llukmkv49c1h">
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-6370" data-mp="1zmx7v0xuksdz">
                      {isQuote ? 'Detalles' : 'Detalles del Pago'}
                    </h3>
                    <p className="text-sm text-gray-800" data-id="element-6371" data-mp="1ulzqgqknw2dj">
                      <span className="font-semibold" data-id="element-6372" data-mp="29bf3z4v6mbba">Tipo:</span>{' '}
                      {formData.paymentType}
                    </p>
                    {!isQuote && <p className="text-sm text-gray-800" data-id="element-6373" data-mp="2icvb93qp9xba">
                        <span className="font-semibold" data-id="element-6374" data-mp="2ib26jtzndn5x">Método:</span>{' '}
                        {formData.paymentMethod}
                      </p>}
                    {!isQuote && formData.paymentReference && <p className="text-sm text-gray-800" data-id="element-6375" data-mp="1fzgz0hmnvszd">
                        <span className="font-semibold" data-id="element-6376" data-mp="1fxmfn7r1g8xe">Referencia:</span>{' '}
                        {formData.paymentReference}
                      </p>}
                  </div>
                  {formData.notes && <div data-id="element-6377" data-mp="1ts6pozd4xm2t">
                      <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-6378" data-mp="1tqvliyfs2cb6">
                        Notas
                      </h3>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap" data-id="element-6379" data-mp="23pun5x24l7oo">
                        {formData.notes}
                      </p>
                    </div>}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 pt-6 flex justify-between items-end relative" data-id="element-6380" data-mp="9d4rpaw3zqf6">
                  <div data-id="element-6381" data-mp="ddf7ff9pdv78">
                    <p className="text-xs text-gray-400 font-medium" data-id="element-6382" data-mp="deym51fpny6w">
                      {isQuote ? 'Este documento es una cotización y no constituye un comprobante de pago.' : 'Este recibo es un comprobante de pago válido.'}
                    </p>
                    <p className="text-xs text-gray-400 font-medium mt-1" data-id="element-6383" data-mp="3s6usa6mq5nlm">
                      Generado por NOIZA - Plataforma de Delivery Digital
                    </p>
                  </div>

                  {/* Watermark/Stamp */}
                  <div className="absolute right-0 bottom-4 opacity-10 rotate-[-15deg] pointer-events-none" data-id="element-6384" data-mp="jt5j6jpi3t9l">
                    <div className="border-4 border-black rounded-lg px-6 py-2" data-id="element-6385" data-mp="fqqzxyhx6rhs">
                      <span className="text-4xl font-black tracking-widest uppercase" data-id="element-6386" data-mp="atik44k8uzjp">
                        {isQuote ? 'Cotización' : formData.paymentAmount >= effectiveTotal ? 'Pagado' : 'Abono'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}